import React, { useState } from "react";
import { Col, Row, UiBox, UiButton, UiCenter, UiIcon, UiInput, UiSimpleGrid, UiText } from "~/components/elements";
import { useAppSelector } from "~/hooks";
import { JobApiItem } from "~/types/models/dashboard";
import config from "~/config/app-config";
import { PAGES } from "~/constants";
// eslint-disable-next-line no-restricted-imports
import { BoxProps } from "@chakra-ui/react";
import { BiSolidMessage } from "react-icons/bi";
import { MdEmail } from "react-icons/md";
import { FaFacebookF, FaLinkedinIn, FaTwitter } from "react-icons/fa";

interface ShareBoxProps extends BoxProps {
  job: JobApiItem | null;
}

function OptionBox({ title, icon, onClick }: { onClick: () => void; title: string; icon: any }) {
  return (
    <Row
      flexDirection={{ base: "row", sm: "column" }}
      gap={{ base: 2, sm: 4 }}
      p={4}
      aspectRatio={2.7}
      // w="full"
      borderWidth="1px"
      borderRadius="1rem"
      borderColor="neutral.gray100"
      cursor="pointer"
      align="center"
      justifyContent="center"
      onClick={onClick}
    >
      <UiCenter rounded="full" w={{ base: 6, sm: 9 }} h={{ base: 6, sm: 9 }} bg="green">
        <UiIcon as={icon} mx={0} color="darkblue" boxSize={{ base: 3, sm: 5 }} />
      </UiCenter>
      <Col flex={1}>
        <UiText vr="title1" textAlign="center" textOverflow="ellipsis" wordBreak="break-all" noOfLines={1}>
          {title}
        </UiText>
      </Col>
    </Row>
  );
}

export function ShareBox({ job, ...rest }: ShareBoxProps) {
  const currentUser = useAppSelector(state => state.auth.currentUser);
  const [name, domain] = currentUser?.email?.split("@") || ["", ""];
  const sharedLink = `${config.url}/s${PAGES.VOTES}/${job?.slug}`;
  const [isCopied, setIsCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(sharedLink).then(() => {
      setIsCopied(true);
      setTimeout(() => {
        setIsCopied(false);
      }, 1500);
    });
  };

  return (
    <UiBox p={4} bg="neutral.gray00" rounded="xl" {...rest}>
      <UiText vr="title1" color="neutral.gray800">
        Send this link to get feedback on your favorite photos:
      </UiText>
      <UiSimpleGrid minChildWidth="7rem" spacing={{ base: 2, sm: 5 }} w="full" mt={4}>
        <OptionBox
          title="Messages"
          icon={BiSolidMessage}
          onClick={() => {
            const body = `Hey! I just got new photos using Bettershot and I need your help picking the best one! Click the link to vote on your favorites: ${sharedLink}`;
            window.open(`sms://;?&body=${body}`);
          }}
        />
        <OptionBox
          title="Email"
          icon={MdEmail}
          onClick={() => {
            const body = `Hey! I just got new photos using Bettershot and I need your help picking the best one! Click the link to vote on your favorites: ${sharedLink}`;
            window.open(`mailto:?to=&body=${body}`, "mail");
          }}
        />
        <OptionBox
          title="Facebook"
          icon={FaFacebookF}
          onClick={() => {
            window.open(`https://www.facebook.com/sharer/sharer.php?u=${sharedLink}`, "_blank");
          }}
        />
        <OptionBox
          title="LinkedIn"
          icon={FaLinkedinIn}
          onClick={() => {
            window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${sharedLink}`, "_blank");
          }}
        />
        <OptionBox
          title="Twitter"
          icon={FaTwitter}
          onClick={() => {
            const body = `Hey! I just got new photos using Bettershot and I need your help picking the best one! Click the link to vote on your favorites: ${sharedLink}`;
            window.open(`https://twitter.com/intent/tweet?text=${body}`, "_blank");
          }}
        />
      </UiSimpleGrid>
      <UiText vr="body2" mt={4}>
        Share link:
      </UiText>
      <Row mt={2}>
        <UiInput value={sharedLink} w="full" h="2.75rem" minH={0} isReadOnly />
        <UiButton h="2.75rem" bg="#2196F3" ml={1} px="5" onClick={handleCopy}>
          {isCopied ? "Copied" : "Copy"}
        </UiButton>
      </Row>
    </UiBox>
  );
}
